import {
  currencyFormat,
  formatAttributeItemValue,
  formatNumberWithSeparator,
} from '@hyphengroup/cms-components/utils/helpers';
import { getHighersRewardExclusiveOffersAndWelcomeOffersGitsFromProduct } from '@hyphengroup/cms-components/utils/rewards';
import {
  BaseAttribute,
  CmsProduct,
  VerticalType,
} from '$/(cms)/_modules/cmsService/types';

export type SharedAttributeData = {
  value: string;
  tooltip: string;
};

export type RewardBadgeStaticText = {
  rewardBadgePrefix: string;
  rewardBadgeSuffix: string;
};

const EMPTY_SHARED_ATTRIBUTE: SharedAttributeData = { value: '', tooltip: '' };

const getAttributeName = (attribute: BaseAttribute): string => {
  switch (attribute?.__typename) {
    case 'ComponentAttributesAttrRichTextEditor':
    case 'ComponentAttributesAttrNumberRange':
    case 'ComponentAttributesAttrNumber':
    case 'ComponentAttributesAttrBoolean':
    case 'ComponentAttributesAttrPlainText':
    case 'ComponentAttributesAttrKeyValue':
    case 'ComponentAttributesAttrJson':
      return attribute.name?.name ?? '';
    default:
      return attribute.__typename ?? '';
  }
};

const getAttributeAdditionalInfo = (attribute: BaseAttribute): string => {
  switch (attribute.__typename) {
    case 'ComponentAttributesAttrNumber':
    case 'ComponentAttributesAttrNumberRange':
      return attribute.additional_information ?? '';
    default:
      return '';
  }
};

const formatRewardAmount = (props: {
  prefix: Parameters<typeof currencyFormat>[0];
  price: number;
}): string => {
  return `${currencyFormat(props.prefix)}${formatNumberWithSeparator({
    value: props.price,
    thousandsSeparator: ',',
  })}`;
};

export const getProductSharedAttributeData = (
  product: CmsProduct,
  sharedAttributeName?: string,
): SharedAttributeData => {
  if (!sharedAttributeName) {
    return EMPTY_SHARED_ATTRIBUTE;
  }

  const foundAttribute = product.attributes?.find(
    (attr) => getAttributeName(attr) === sharedAttributeName,
  );
  if (!foundAttribute) {
    return EMPTY_SHARED_ATTRIBUTE;
  }

  const formattedValue = formatAttributeItemValue(
    foundAttribute as unknown as BaseAttribute,
  ).trim();

  return {
    value:
      formattedValue === '-' || formattedValue === '- -' ? '' : formattedValue,
    tooltip: getAttributeAdditionalInfo(foundAttribute),
  };
};

export const getProductRewardBadgeCopy = (props: {
  product: CmsProduct;
  staticText: RewardBadgeStaticText;
  vertical?: VerticalType;
  overrideBadge?: string;
}): string => {
  const { product, staticText, vertical, overrideBadge } = props;

  if (overrideBadge?.trim()) {
    return overrideBadge.trim();
  }

  const rewards =
    getHighersRewardExclusiveOffersAndWelcomeOffersGitsFromProduct(
      product,
      undefined,
      vertical !== 'PERSONAL_LOANS',
    );

  if (!rewards?.price) {
    return '';
  }

  const amount = formatRewardAmount({
    prefix: rewards.prefix,
    price: rewards.price,
  });

  return `${staticText.rewardBadgePrefix} ${amount} ${staticText.rewardBadgeSuffix}`;
};
